import React, { useMemo } from 'react';
import * as d3 from 'd3';
import {
  Grid,
  Paper,
  Typography,
  Box,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { ResizableTable } from '../../components/resizableTable/ResizableTable';
import { dateFormatterMomentV3 } from '../../util/customFunctions';
import { flexToWidth } from '../../util/utils';

// Build ResizableTable column definitions from LKP_DRILLDOWN_CONFIG records
const buildForecastColumnsFromConfig = (configColumns = []) => {
  if (!configColumns || configColumns.length === 0) return null;

  const columns = [
    {
      label: '#',
      sortable: false,
      align: 'center',
      subColumns: [{ label: '#', field: 'rowNum', type: 'number', align: 'center', width: 10, sortable: false }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    },
  ];

  configColumns.forEach((col) => {
    let type = 'text';
    let align = 'left';
    if (col.type === 'currency' || col.type === 'integer') { type = 'number'; align = 'right'; }
    else if (col.type === 'percent') { type = 'percentage'; align = 'right'; }
    else if (col.type === 'date' || col.type === 'datetime') { type = 'date'; align = 'right'; }

    // Use explicit align from LKP_DRILLDOWN_CONFIG if provided
    // Values are AG Grid class names: 'leftAligned', 'rightAligned', 'centerAligned'
    if (col.align) {
      if (col.align === 'leftAligned') align = 'left';
      else if (col.align === 'rightAligned') align = 'right';
      else if (col.align === 'centerAligned') align = 'center';
      else align = col.align; // direct value fallback ('left', 'right', 'center')
    }

    const width = flexToWidth(col.flex);

    columns.push({
      label: col.label,
      sortable: true,
      align: 'center',
      subColumns: [{ label: col.label, field: col.field, type, align, width, sortable: true, filterable: true }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    });
  });

  return columns;
};

export { buildForecastColumnsFromConfig };

// Column definitions for Won Opportunities table (uses raw DB field names)
const createWonOppsColumns = (currencySymbol = '') => [
  {
    label: '#',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'center',
      width: 5,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Opp Name',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Opportunity',
      field: 'OppName',
      type: 'text',
      align: 'left',
      width: 120,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Owner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Owner',
      field: 'Owner_Name',
      type: 'text',
      align: 'left',
      width: 50,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: `ACV (${currencySymbol}K)`,
    sortable: true,
    align: 'center',
    subColumns: [{
      label: `ACV (${currencySymbol}K)`,
      field: 'ACV',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Won Date',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Won Date',
      field: 'wonDate',
      type: 'date',
      align: 'right',
      width: 40,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Age since Qual. (Days)',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Age since Qual. (Days)',
      field: 'ageSinceQualDays',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Partner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Partner',
      field: 'Partner_Name',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Column definitions for Summary table
const createSummaryTableColumns = (currencySymbol = '') => [
  {
    label: '',
    sortable: false,
    subColumns: [{
      label: '',
      field: 'label',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: '# of Opps',
    sortable: false,
    subColumns: [{
      label: '# of Opps',
      field: 'count',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: `ACV (${currencySymbol}M)`,
    sortable: false,
    subColumns: [{
      label: `ACV (${currencySymbol}M)`,
      field: 'acvInMillions',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

const SalesForecastCurrentQuarterTable = ({ classes, data, isLoading, asOfDateLabel, handleChangeLocation, currencySymbol = '', forecastOpportunityColumns = [], wonOpportunityColumns = [], enableDrilldown = true }) => {
  const formatDateToMMDDYYYY = (dateString) => {
    if (!dateString || dateString === '') return '';
    try {
      return dateFormatterMomentV3(dateString);
    } catch (error) {
      return '';
    }
  };

  // Build won opps columns from config (same pattern as forecast opps)
  const wonOppsColumns = useMemo(
    () => wonOpportunityColumns && wonOpportunityColumns.length > 0
      ? buildForecastColumnsFromConfig(wonOpportunityColumns)
      : createWonOppsColumns(currencySymbol),
    [wonOpportunityColumns, currencySymbol]
  );

  // Transform won opportunities data to rows format
  const wonOppsRows = useMemo(() => {
    if (!data?.wonOpps || data.wonOpps.length === 0) {
      return [];
    }
    return data.wonOpps.map((opp, index) => ({
      ...opp,
      label: opp.Opp_ID || `won_${index}`,
    }));
  }, [data?.wonOpps]);

  // Forecast opportunity rows — spread raw DB fields, add label key
  const forecastOppsRows = useMemo(() => {
    if (!data?.forecastOpps || data.forecastOpps.length === 0) return [];
    return data.forecastOpps.map((opp, index) => ({
      ...opp,
      label: opp.Opp_ID || `forecast_${index}`,
    }));
  }, [data?.forecastOpps]);

  // Build ResizableTable columns from LKP_DRILLDOWN_CONFIG config
  const forecastOppsColumns = useMemo(
    () => buildForecastColumnsFromConfig(forecastOpportunityColumns),
    [forecastOpportunityColumns]
  );

  return (
    <Box mt={4}>
      <Paper className={classes.paper}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <Typography variant="h6" style={{ fontWeight: 600, fontSize: '1.2rem' }}>
            {data?.title || 'Sales Forecast – Current Quarter'}
          </Typography>
          <span className={classes.updateFrequency}>
            {'Live Data'}
          </span>
        </Box>

        {isLoading ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Skeleton variant="rect" animation="wave" height={200} width="100%" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rect" animation="wave" height={300} width="100%" />
            </Grid>
          </Grid>
        ) : (
          <>
            {/* Summary Table */}
            <Box mb={3}>
              <Typography variant="subtitle1" style={{ fontWeight: 600}}>
                Summary
              </Typography>
              <ResizableTable
                columns={createSummaryTableColumns(currencySymbol)}
                rows={[
                  {
                    label: 'Won QTD',
                    count: data?.summary?.won?.count || 0,
                    acvInMillions: data?.summary?.won?.acvInMillions || '0.00',
                    rowType: 'won',
                  },
                  {
                    label: 'Most Likely + Commit',
                    count: data?.summary?.forecast?.count || 0,
                    acvInMillions: data?.summary?.forecast?.acvInMillions || '0.00',
                    rowType: 'forecast',
                  },
                  {
                    label: 'Total Forecast',
                    count: data?.summary?.total?.count || 0,
                    acvInMillions: data?.summary?.total?.acvInMillions || '0.00',
                    rowType: 'total',
                  },
                   {
                      label: 'Forecast Goal (1.5x Quota)',
                      count: '',
                      acvInMillions: data?.summary?.forecastGoal || '0.00',
                      rowType: 'forecast',
                    },
                  {
                    label: 'Quota',
                    count: '',
                    acvInMillions: data?.summary?.quotaInMillions || '0.00',
                    rowType: 'quota',
                  },
                ]}
                getCellValue={(row, field) => {
                  if (field === 'label') {
                    return row[field];
                  }
                  return row[field] != null ? row[field] : '';
                }}
                getCellStyle={(row, field) => {
                  const baseStyle = {};
                  if (field === 'label') {
                    baseStyle.fontWeight = 600;
                  }
                  if (row.rowType === 'total') {
                    baseStyle.backgroundColor = '#f5f5f5';
                    if (field === 'count' || field === 'acvInMillions') {
                      baseStyle.fontWeight = 600;
                    }
                  }
                  if (row.rowType === 'quota') {
                    baseStyle.backgroundColor = '#fff9c4';
                    if (field === 'acvInMillions') {
                      baseStyle.fontWeight = 600;
                    }
                  }
                  return baseStyle;
                }}
                rowKey="label"
                hideSubHeaderRow={true}
                maxWidth="34%"
              />
            </Box>

            {/* Won Opportunities Table */}
            <Box mb={3}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 70 }}>
                Won Opportunities
              </Typography>
              {wonOppsRows.length > 0 ? (
                <ResizableTable
                  columns={wonOppsColumns}
                  rows={wonOppsRows}
                  getCellValue={(row, field, rowIndex) => {
                    if (field === 'rowNum') {
                      return rowIndex != null ? rowIndex + 1 : row[field];
                    }
                    const colConfig = wonOpportunityColumns && wonOpportunityColumns.find(c => c.field === field);
                    const colType = colConfig?.type;
                    if (colType === 'date' || colType === 'datetime') {
                      return formatDateToMMDDYYYY(row[field]);
                    }
                    if (colType === 'currency') {
                      return row[field] != null ? d3.format(",.1f")(row[field] / 1000) : '';
                    }
                    if (colType === 'integer') {
                      if (row[field] == null) return '';
                      const num = typeof row[field] === 'number' ? row[field] : parseFloat(row[field]);
                      return !isNaN(num) ? d3.format(",")(num) : row[field];
                    }
                    // Default formatters for known field names (matches SQL aliases and config)
                    if (field === 'ACV') return row[field] != null ? d3.format(",.1f")(row[field] / 1000) : '';
                    if (field === 'wonDate') return formatDateToMMDDYYYY(row[field]);
                    if (field === 'ageSinceQualDays') return row[field] != null ? d3.format(",")(row[field]) : '';
                    return row[field] != null ? row[field] : '';
                  }}
                  getCellStyle={(row) => {
                    if (row.Is_New_This_Week === 'Y') return { backgroundColor: '#c8e6c9' };
                    return {};
                  }}
                  rowKey="Opp_ID"
                  hideSubHeaderRow={true}
                  maxWidth="100%"
                  onRowClick={enableDrilldown ? (row) => handleChangeLocation && handleChangeLocation("drilldownOnGraph", row, 'currentQuarterWon') : undefined}
                />
              ) : (
                <Box style={{ textAlign: 'center', fontStyle: 'italic', color: '#666', padding: '20px' }}>
                  No won opportunities this quarter
                </Box>
              )}
            </Box>

            {/* Forecast Opportunities Table */}
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 70 }}>
                Forecast Opportunities (Most Likely + Commit Opportunities)
              </Typography>
              {forecastOppsRows.length > 0 && forecastOppsColumns ? (
                <ResizableTable
                  columns={forecastOppsColumns}
                  rows={[
                    ...forecastOppsRows,
                    {
                      isTotal: true,
                      label: '__total__',
                      OppName: `Total (${data?.summary?.forecast?.count ?? forecastOppsRows.length} opps)`,
                      Acv_At_Time_T: data?.summary?.forecast?.acv ?? 0,
                      ACV: data?.summary?.forecast?.acv ?? 0,
                    },
                    {
                      isQuota: true,
                      label: '__quota__',
                      OppName: 'Quota',
                      Acv_At_Time_T: data?.summary?.quota ?? 0,
                      ACV: data?.summary?.quota ?? 0,
                    },
                  ]}
                  getCellValue={(row, field, rowIndex) => {
                    if (row.isTotal) {
                      if (field === 'rowNum') return '-';
                      if (field === 'OppName') return row.OppName;
                      if (field === 'Acv_At_Time_T' || field === 'ACV') {
                        const val = row[field];
                        return val != null ? d3.format(",.1f")(val / 1000) : '';
                      }
                      return '';
                    }
                    if (row.isQuota) {
                      if (field === 'rowNum') return '-';
                      if (field === 'OppName') return 'Quota';
                      if (field === 'Acv_At_Time_T' || field === 'ACV') {
                        const val = row[field];
                        return val != null ? d3.format(",.1f")(val / 1000) : '';
                      }
                      return '';
                    }
                    if (field === 'rowNum') {
                      return rowIndex != null ? rowIndex + 1 : row[field];
                    }
                    if (field === 'Projected_Close_Date_At_Time' || field === 'Projected_Close_Date') {
                      return formatDateToMMDDYYYY(row[field]);
                    }
                    if (field === 'Acv_At_Time_T' || field === 'ACV') {
                      return row[field] != null ? d3.format(",.1f")(row[field] / 1000) : '';
                    }
                    if (field === 'Days_Since_Qualified' || field === 'Days_at_Stage') {
                      return row[field] != null ? d3.format(",")(row[field]) : '';
                    }
                    return row[field] != null ? row[field] : '';
                  }}
                  getCellStyle={(row) => {
                    if (row.isTotal) {
                      return { backgroundColor: '#f5f5f5', fontWeight: 700 };
                    }
                    if (row.isQuota) {
                      return { backgroundColor: '#fff9c4', fontWeight: 700 };
                    }
                    if (row.Is_New_This_Week === 'Y') {
                      return { backgroundColor: '#c8e6c9' };
                    }
                    return {};
                  }}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="100%"
                  onRowClick={enableDrilldown ? (row) => {
                    if (row.isTotal || row.isQuota) return;
                    handleChangeLocation && handleChangeLocation("drilldownOnGraph", row, 'currentQuarterForecast');
                  } : undefined}
                />
              ) : (
                <Box style={{ textAlign: 'center', fontStyle: 'italic', color: '#666', padding: '20px' }}>
                  No forecast opportunities for this quarter
                </Box>
              )}
            </Box>

            {/* Legend for green highlighting */}
            <Box mt={2}>
              <div className={classes.legend}>
                <div className={classes.legendItem}>
                  <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                  <Typography variant="body2">New this week</Typography>
                </div>
              </div>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SalesForecastCurrentQuarterTable;
